import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { ShoppingBag, Store } from 'lucide-react';

/**
 * PublicLayout — shared header + footer for every public-facing page
 * (homepage, catalog, category, item detail, post-demand).
 *
 * The marketing sub-pages (Buyers, Vendors, Pricing…) live inside Website.js
 * as internal sections; clicking them from elsewhere navigates to `/?page=X`
 * and Website.js picks the section up from the query param. Real routes
 * (Browse Items, Bulk, Login, Register) use <Link>. This preserves SEO —
 * every page has a discoverable URL and the nav is a single consistent
 * internal-link surface for crawlers.
 */

const C = {
  green: '#1B5C3A', greenDark: '#0F3D26', greenLight: '#E8F5EE',
  gold: '#F5A623', goldLight: '#FFF8E7', goldDark: '#C4831A',
  cream: '#FAF8F3', charcoal: '#1A1A1A', muted: '#6B7280',
  border: '#E5E7EB', white: '#FFFFFF',
};
const F = { display: "'Fraunces', Georgia, serif", body: "'Plus Jakarta Sans', sans-serif" };

// Each marketing page has its own real URL — no more ?page= query-param hack.
const NAV = [
  { label: 'Buyers', path: '/buyers' },
  { label: 'Vendors', path: '/vendors' },
  { label: 'Bulk Sales', path: '/bulk-sales' },
  // { label: 'Services',   path: '/services' },
  // { label: 'Pricing',    path: '/pricing' },
  // { label: 'Blog',       path: '/blog' },
  // { label: 'About',      path: '/about' },
  // { label: 'Contact',    path: '/contact' },
];

function Logo({ light = false, size = 1 }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
      <img
        src="/logo-1.png"
        alt="Kanjusee"
        style={{
          height: 40 * size,
          width: 40 * size,
          objectFit: 'contain',
          borderRadius: 10,
          flexShrink: 0
        }}
      />
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-start' }}>
        <div style={{
          fontFamily: F.display,
          fontSize: 22 * size,
          fontWeight: 900,
          lineHeight: 1,
          letterSpacing: -0.5,
          color: light ? C.white : C.green
        }}>
          Kanjusee<span style={{ color: C.gold }}>.</span>
        </div>
        <div
          style={{
            fontFamily: F.body,
            fontSize: 9 * size,
            fontWeight: 600,
            letterSpacing: 1,
            marginTop: 2,
            color: light ? 'rgba(255,255,255,0.55)' : C.muted
          }}
        >
          SASTA HAI TO BANTA HAI
        </div>
      </div>
    </div>
  );
}

function NavBtn({ children, to, variant = 'outline', onClick, style }) {
  const [h, setH] = useState(false);
  const styles = {
    primary: { background: h ? C.greenDark : C.green, color: C.white, border: 'none' },
    gold: { background: h ? C.goldDark : C.gold, color: C.charcoal, border: 'none' },
    outline: { background: 'transparent', color: C.green, border: `1.5px solid ${C.green}`, opacity: h ? 0.8 : 1 },
  };
  const base = {
    fontFamily: F.body, fontWeight: 700, fontSize: 13,
    padding: '8px 16px', borderRadius: 9, cursor: 'pointer',
    whiteSpace: 'nowrap', transition: 'all 0.15s',
    textDecoration: 'none', display: 'inline-flex', alignItems: 'center', gap: 6,
    ...styles[variant], ...style,
  };
  const handlers = { onMouseEnter: () => setH(true), onMouseLeave: () => setH(false) };
  if (to) return <Link to={to} style={base} {...handlers}>{children}</Link>;
  return <button onClick={onClick} style={base} {...handlers}>{children}</button>;
}

export function PublicNav() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [menuOpen, setMenuOpen] = useState(false);

  const navLinks = (
    <>
      {NAV.map(n => {
        const active = location.pathname === n.path;
        return (
          <Link
            key={n.path}
            to={n.path}
            onClick={() => setMenuOpen(false)}
            style={{
              fontFamily: F.body, fontSize: 13, fontWeight: 600,
              padding: '6px 11px', borderRadius: 8,
              whiteSpace: 'nowrap', textDecoration: 'none',
              background: active ? C.greenLight : 'transparent',
              color: active ? C.green : C.muted,
              transition: 'all 0.15s',
              display: 'block',
              marginBottom: menuOpen ? 8 : 0
            }}
          >
            {n.label}
          </Link>
        );
      })}
    </>
  );

  const authActions = (isMobile = false) => (
    <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', gap: 12, alignItems: isMobile ? 'stretch' : 'center' }}>
      <NavBtn to="/catalog" variant="primary" style={{ width: isMobile ? '100%' : 'auto' }}>Browse Items</NavBtn>
      <NavBtn to="/bulk" variant="gold" style={{ width: isMobile ? '100%' : 'auto' }}>Bulk Sales</NavBtn>
      {user ? (
        <NavBtn
          variant="outline"
          style={{ width: isMobile ? '100%' : 'auto' }}
          onClick={() => {
            setMenuOpen(false);
            navigate(user.role === 'vendor' ? '/vendor/dashboard' : user.role === 'admin' ? '/admin/dashboard' : '/buyer/home');
          }}
        >
          Dashboard
        </NavBtn>
      ) : (
        <>
          <Link to="/vendor/login" onClick={() => setMenuOpen(false)} style={{ fontFamily: F.body, fontSize: 12, fontWeight: 700, color: C.muted, textDecoration: 'none', padding: '6px 10px', borderRadius: 8, whiteSpace: 'nowrap', textAlign: isMobile ? 'center' : 'left' }}>Vendor Login</Link>
          <NavBtn to="/login" variant="outline" onClick={() => setMenuOpen(false)} style={{ width: isMobile ? '100%' : 'auto' }}>🛍 Buyer Login</NavBtn>
          <NavBtn to="/register" variant="gold" onClick={() => setMenuOpen(false)} style={{ width: isMobile ? '100%' : 'auto' }}>Get Started →</NavBtn>
        </>
      )}
    </div>
  );

  return (
    <nav style={{ background: C.white, borderBottom: `1px solid ${C.border}`, position: 'sticky', top: 0, zIndex: 200, boxShadow: '0 1px 12px rgba(0,0,0,0.05)' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 24px', height: 64, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        <Link to="/" style={{ textDecoration: 'none' }} aria-label="Kanjusee home"><Logo /></Link>

        {/* Desktop Nav */}
        <div className="hide-mobile" style={{ flex: 1, display: 'flex', alignItems: 'center', gap: 16, marginLeft: 24 }}>
          <div style={{ flex: 1, display: 'flex', gap: 4, overflowX: 'auto' }}>
            {navLinks}
          </div>
          {authActions()}
        </div>

        {/* Mobile Hamburger */}
        <button
          className="show-mobile"
          onClick={() => setMenuOpen(true)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 8 }}
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="12" x2="21" y2="12"></line>
            <line x1="3" y1="6" x2="21" y2="6"></line>
            <line x1="3" y1="18" x2="21" y2="18"></line>
          </svg>
        </button>
      </div>

      {/* Mobile Drawer */}
      {menuOpen && (
        <>
          <div className="mobile-menu-overlay" onClick={() => setMenuOpen(false)} />
          <div className="mobile-menu-drawer">
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 32 }}>
              <Logo size={0.8} />
              <button onClick={() => setMenuOpen(false)} style={{ background: 'var(--green-lt)', border: 'none', borderRadius: '50%', width: 36, height: 36, display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer' }}>
                <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke={C.green} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="18" y1="6" x2="6" y2="18"></line>
                  <line x1="6" y1="6" x2="18" y2="18"></line>
                </svg>
              </button>
            </div>
            <div style={{ flex: 1, overflowY: 'auto' }}>
              <div style={{ marginBottom: 32 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: C.goldDark, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 16 }}>Marketplace</div>
                {navLinks}
              </div>
              <div style={{ borderTop: `1.5px solid ${C.border}`, paddingTop: 32 }}>
                <div style={{ fontSize: 10, fontWeight: 800, color: C.goldDark, textTransform: 'uppercase', letterSpacing: 2, marginBottom: 16 }}>Your Account</div>
                {authActions(true)}
              </div>
            </div>
          </div>
        </>
      )}
    </nav>
  );
}

// Footer labels → real paths. Fallback entries point legal/careers labels
// at About/Contact until dedicated pages exist.
const FOOTER_LINK_PATH = {
  Buyers: '/buyers', Vendors: '/vendors', 'Bulk Sales': '/bulk-sales',
  Services: '/services', Pricing: '/pricing', Blog: '/blog',
  About: '/about', Contact: '/contact',
  Careers: '/about', 'Partner with Us': '/contact', Press: '/contact',
  'Terms of Use': '/terms', 'Privacy Policy': '/privacy', 'Disclaimer': '/disclaimer',
  'Refund Policy': '/refund-policy', 'Responsibilities': '/responsibilities', 'Grievance': '/grievance',
  'Buyer Login': '/login', 'Vendor Login': '/vendor/login',
  'Create Buyer Account': '/register', 'Create Vendor Account': '/vendor-signup',
  'Recent Demands': '/recent-demands',
};

export function PublicFooter() {
  return (
    <footer style={{ background: C.greenDark, padding: '64px 24px 32px' }}>
      <div style={{ maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: 40, marginBottom: 52 }}>
          <div style={{ gridColumn: 'span 1' }}>
            <Link to="/" style={{ textDecoration: 'none' }} aria-label="Kanjusee home"><Logo light /></Link>
            <p style={{ fontFamily: F.body, fontSize: 14, color: 'rgba(255,255,255,0.55)', lineHeight: 1.75, margin: '18px 0 24px', maxWidth: 290 }}>
              India's first buyer-powered marketplace. Post your demand at your price. Verified vendors from 180+ cities across India compete to win your order.
            </p>
            <div style={{ fontFamily: F.body, fontSize: 12, color: 'rgba(255,255,255,0.35)', lineHeight: 1.7 }}>
              Operated by Munshee Digi Venture Pvt Ltd.<br />906 International Finance Center, Opp Vesu Fire Station,<br />Vesu, Surat (Guj) 395009, India<br />Ph: +91 8000223315 · info@kanjusee.com
            </div>
          </div>
          {[
            ['Platform', ['Buyers', 'Vendors', 'Bulk Sales', 'Services', 'Pricing', 'Blog', 'Recent Demands']],
            ['Sign In', ['Buyer Login', 'Vendor Login', 'Create Buyer Account', 'Create Vendor Account']],
            ['Company', ['About', 'Contact', 'Careers', 'Partner with Us', 'Press']],
            ['Legal', ['Terms of Use', 'Privacy Policy', 'Disclaimer', 'Refund Policy', 'Responsibilities', 'Grievance']],
          ].map(([title, links]) => (
            <div key={title}>
              <div style={{ fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.gold, textTransform: 'uppercase', letterSpacing: 2.5, marginBottom: 18 }}>{title}</div>
              {links.map(l => (
                <Link
                  key={l}
                  to={FOOTER_LINK_PATH[l] || '/'}
                  style={{ display: 'block', fontFamily: F.body, fontSize: 13, color: 'rgba(255,255,255,0.5)', marginBottom: 11, textDecoration: 'none' }}
                  onMouseEnter={e => (e.target.style.color = C.white)}
                  onMouseLeave={e => (e.target.style.color = 'rgba(255,255,255,0.5)')}
                >
                  {l}
                </Link>
              ))}
            </div>
          ))}
        </div>

        {/* App Download Banner */}
        <div style={{
          borderTop: '1px solid rgba(255,255,255,0.09)',
          paddingTop: 32,
          paddingBottom: 8,
          marginBottom: 32,
          display: 'flex',
          flexWrap: 'wrap',
          justifyContent: 'space-between',
          alignItems: 'center',
          gap: 24
        }}>
          <div>
            <div style={{ fontFamily: F.body, fontSize: 16, fontWeight: 700, color: C.white, marginBottom: 6 }}>
              For better experience, download the Kanjusee app now
            </div>
            <div style={{ fontFamily: F.body, fontSize: 13, color: 'rgba(255,255,255,0.45)', maxWidth: 500 }}>
              Post demands, bid on listings, and bargain with local vendors instantly. Available for both Buyers and Sellers.
            </div>
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 32 }}>
            {/* Buyer App */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.gold, textTransform: 'uppercase', letterSpacing: 1.5 }}>
                <ShoppingBag size={14} color="#4ADE80" /> Buyer App
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Play Store */}
                  <a href="https://play.google.com/store/apps/details?id=com.gs.kanjusee.buyer" target="_blank" rel="noopener noreferrer" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 10,
                    background: '#0a0a0a',
                    color: C.white,
                    border: '1px solid rgba(255,255,255,0.12)',
                    padding: '8px 16px',
                    borderRadius: 8,
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.background = '#111'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.background = '#0a0a0a'; }}
                  >
                    <svg width="22" height="22" viewBox="0 0 466 511.98" style={{ flexShrink: 0 }}>
                      <g fillRule="nonzero">
                        <path fill="#EA4335" d="M199.9 237.8 1.4 470.17c7.22 24.57 30.16 41.81 55.8 41.81 11.16 0 20.93-2.79 29.3-8.37l244.16-139.46L199.9 237.8z" />
                        <path fill="#FBBC04" d="m433.91 205.1-104.65-60-111.61 110.22 113.01 108.83 104.64-58.6c18.14-9.77 30.7-29.3 30.7-50.23-1.4-20.93-13.95-40.46-32.09-50.22z" />
                        <path fill="#34A853" d="M199.42 273.45 329.27 145.1 87.9 8.37C79.53 2.79 68.36 0 57.2 0 30.7 0 6.98 18.14 1.4 41.86l198.02 231.59z" />
                        <path fill="#4285F4" d="M1.39 41.86C0 46.04 0 51.63 0 57.2v397.64c0 5.57 0 9.76 1.4 15.34l216.27-214.86L1.39 41.86z" />
                      </g>
                    </svg>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 8, textTransform: 'uppercase', opacity: 0.6, letterSpacing: 0.5, lineHeight: 1 }}>Get it on</div>
                      <div style={{ fontSize: 12, fontWeight: 700, fontFamily: F.body, marginTop: 2, lineHeight: 1 }}>Google Play</div>
                    </div>
                  </a>

                  {/* App Store */}
                  <a href="https://apps.apple.com/us/app/kanjusee/id6782860992" target="_blank" rel="noopener noreferrer" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 10,
                    background: '#0a0a0a',
                    color: C.white,
                    border: '1px solid rgba(255,255,255,0.12)',
                    padding: '8px 16px',
                    borderRadius: 8,
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.background = '#111'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.background = '#0a0a0a'; }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor"><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 4.17c.66-.81 1.11-1.93.99-3.06-1 .04-2.22.67-2.94 1.5-.64.74-1.2 1.88-1.05 2.99 1.12.09 2.26-.57 3-1.43z" /></svg>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 8, textTransform: 'uppercase', opacity: 0.6, letterSpacing: 0.5, lineHeight: 1 }}>Download on the</div>
                      <div style={{ fontSize: 12, fontWeight: 700, fontFamily: F.body, marginTop: 2, lineHeight: 1 }}>App Store</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>

            {/* Vendor App */}
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontFamily: F.body, fontSize: 11, fontWeight: 700, color: C.gold, textTransform: 'uppercase', letterSpacing: 1.5 }}>
                <Store size={14} color="#1cbbf0" /> Vendor App
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
                <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
                  {/* Play Store */}
                  <a href="https://play.google.com/store/apps/details?id=com.gs.kanjusee.seller" target="_blank" rel="noopener noreferrer" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 10,
                    background: '#0a0a0a',
                    color: C.white,
                    border: '1px solid rgba(255,255,255,0.12)',
                    padding: '8px 16px',
                    borderRadius: 8,
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.background = '#111'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.background = '#0a0a0a'; }}
                  >
                    <svg width="22" height="22" viewBox="0 0 466 511.98" style={{ flexShrink: 0 }}>
                      <g fillRule="nonzero">
                        <path fill="#EA4335" d="M199.9 237.8 1.4 470.17c7.22 24.57 30.16 41.81 55.8 41.81 11.16 0 20.93-2.79 29.3-8.37l244.16-139.46L199.9 237.8z" />
                        <path fill="#FBBC04" d="m433.91 205.1-104.65-60-111.61 110.22 113.01 108.83 104.64-58.6c18.14-9.77 30.7-29.3 30.7-50.23-1.4-20.93-13.95-40.46-32.09-50.22z" />
                        <path fill="#34A853" d="M199.42 273.45 329.27 145.1 87.9 8.37C79.53 2.79 68.36 0 57.2 0 30.7 0 6.98 18.14 1.4 41.86l198.02 231.59z" />
                        <path fill="#4285F4" d="M1.39 41.86C0 46.04 0 51.63 0 57.2v397.64c0 5.57 0 9.76 1.4 15.34l216.27-214.86L1.39 41.86z" />
                      </g>
                    </svg>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 8, textTransform: 'uppercase', opacity: 0.6, letterSpacing: 0.5, lineHeight: 1 }}>Get it on</div>
                      <div style={{ fontSize: 12, fontWeight: 700, fontFamily: F.body, marginTop: 2, lineHeight: 1 }}>Google Play</div>
                    </div>
                  </a>

                  {/* App Store */}
                  <a href="https://apps.apple.com/us/app/kanjusee-seller/id6782906482" target="_blank" rel="noopener noreferrer" style={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 10,
                    background: '#0a0a0a',
                    color: C.white,
                    border: '1px solid rgba(255,255,255,0.12)',
                    padding: '8px 16px',
                    borderRadius: 8,
                    textDecoration: 'none',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
                  }}
                    onMouseEnter={e => { e.currentTarget.style.borderColor = C.gold; e.currentTarget.style.background = '#111'; }}
                    onMouseLeave={e => { e.currentTarget.style.borderColor = 'rgba(255,255,255,0.12)'; e.currentTarget.style.background = '#0a0a0a'; }}
                  >
                    <svg width="22" height="22" viewBox="0 0 24 24" fill="currentColor" style={{ flexShrink: 0 }}><path d="M18.71 19.5c-.83 1.24-1.71 2.45-3.05 2.47-1.34.03-1.77-.79-3.29-.79-1.53 0-2 .77-3.27.82-1.31.05-2.3-1.32-3.14-2.53C4.25 17 2.94 12.45 4.7 9.39c.87-1.52 2.43-2.48 4.12-2.51 1.28-.02 2.5.87 3.29.87.78 0 2.26-1.07 3.81-.91.65.03 2.47.26 3.64 1.98-.09.06-2.17 1.28-2.15 3.81.03 3.02 2.65 4.03 2.68 4.04-.03.07-.42 1.44-1.38 2.83M15.97 4.17c.66-.81 1.11-1.93.99-3.06-1 .04-2.22.67-2.94 1.5-.64.74-1.2 1.88-1.05 2.99 1.12.09 2.26-.57 3-1.43z" /></svg>
                    <div style={{ textAlign: 'left' }}>
                      <div style={{ fontSize: 8, textTransform: 'uppercase', opacity: 0.6, letterSpacing: 0.5, lineHeight: 1 }}>Download on the</div>
                      <div style={{ fontSize: 12, fontWeight: 700, fontFamily: F.body, marginTop: 2, lineHeight: 1 }}>App Store</div>
                    </div>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div style={{ borderTop: '1px solid rgba(255,255,255,0.09)', paddingTop: 24, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 12 }}>
          <div style={{ fontFamily: F.body, fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>© 2026 Munshee Digi Venture Pvt Ltd. · Kanjusee.com — All rights reserved.</div>
          <div style={{ fontFamily: F.body, fontSize: 12, color: 'rgba(255,255,255,0.3)' }}>🇮🇳 Made in Surat, India · SAC 998361</div>
        </div>
      </div>
    </footer>
  );
}

export default function PublicLayout({ children }) {
  return (
    <div style={{ fontFamily: F.body, background: C.white, minHeight: '100vh', display: 'flex', flexDirection: 'column' }}>
      <PublicNav />
      <main style={{ flex: 1 }}>{children}</main>
      <PublicFooter />
    </div>
  );
}
